<template>
  <div v-if="!isDismissed">
    <div class="Polaris-LegacyCard">
      <div class="Polaris-LegacyCard__Header flex justify-between">
        <div>
          <h2
            class="Polaris-Text--root Polaris-Text--bodyMd Polaris-Text--semibold"
          >
            Setup Guide
          </h2>
          <p class="text-gray-500 mt-1">
            Use this personalized guide to get your app up and running.
          </p>
        </div>
        <button
          class="Polaris-Button Polaris-Button--plain Polaris-Button--iconOnly"
          aria-label="Dismiss onboarding checklist"
          type="button"
          @click="dismissOnboarding"
        >
          <span class="Polaris-Icon">
            <svg
              viewBox="0 0 20 20"
              class="Polaris-Icon__Svg"
              focusable="false"
              aria-hidden="true"
            >
              <path
                d="M11.414 10l4.293-4.293a1 1 0 00-1.414-1.414L10 8.586 5.707 4.293a1 1 0 00-1.414 1.414L8.586 10l-4.293 4.293a1 1 0 101.414 1.414L10 11.414l4.293 4.293a1 1 0 001.414-1.414L11.414 10z"
              ></path>
            </svg>
          </span>
        </button>
      </div>

      <div class="Polaris-LegacyCard__Section">
        <div class="text-gray-500">
          <span
            >{{ completedValue.completedFeatures }} /
            {{ completedValue.totalFeatures }} completed</span
          >

          <div class="mt-4" style="width:225px">
            <div
              class="Polaris-ProgressBar Polaris-ProgressBar--sizeMedium Polaris-ProgressBar--tonePrimary"
              style="--pc-progress-bar-height-base:8px"
            >
              <progress
                class="Polaris-ProgressBar__Progress"
                :value="completedValue.percentage * 100"
                max="100"
              >
              </progress>
              <div
                class="Polaris-ProgressBar__Indicator Polaris-ProgressBar__IndicatorAppearActive"
                style="--pc-progress-bar-duration:500ms;"
                :style="{
                  '--pc-progress-bar-percent': completedValue.percentage
                }"
              >
                <span class="Polaris-ProgressBar__Label"
                  >{{ completedValue.percentage
                  }}<!-- -->%</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feature items -->
      <div
        v-for="(feature, index) in features"
        :key="feature.flag"
        class="Polaris-LegacyCard__Section rounded-md my-2 mx-2 hover:bg-gray-50"
      >
        <div
          class="flex justify-between cursor-pointer"
          @click="toggleFeature(index)"
        >
          <div class="flex items-start w-full">
            <div class="rounded-full border border-gray-300  mr-3 mt-1">
              <div
                class="w-4 h-4 flex items-center justify-center"
                :class="{
                  'bg-gray-900 rounded-full':
                    onboardingFlags && onboardingFlags[feature.flag]
                }"
              >
                <svg
                  v-if="onboardingFlags && onboardingFlags[feature.flag]"
                  class="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <div class="w-full">
              <div class="flex justify-between items-center">
                <h3 class="font-medium">{{ feature.label }}</h3>
              </div>
              <transition
                enter-active-class="transition duration-100 ease-out"
                leave-active-class="transition duration-75 ease-in"
                enter-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <div v-if="feature.isOpen">
                  <p class="text-gray-700 mt-1 mb-4 max-w-lg">
                    {{ feature.description }}
                  </p>

                  <button
                    v-if="!onboardingFlags[feature.flag]"
                    class="Polaris-Button Polaris-Button--pressable Polaris-Button--variantPrimary Polaris-Button--sizeMedium Polaris-Button--textAlignCenter"
                    type="button"
                    @click.stop="feature.button.onClick"
                  >
                    <span
                      class="Polaris-Text--root Polaris-Text--bodySm Polaris-Text--medium"
                      >{{ feature.button.label }}</span
                    >
                  </button>
                </div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "OnboardingChecklist",
  data() {
    return {
      isOpen: false,
      features: [
        {
          label: "Connect your Google Business Account",
          isOpen: true,
          description:
            "When you connect your Google Business account, we'll automatically import your reviews to display on your store",
          flag: "isGoogleConnected",
          button: {
            label: "Connect",
            onClick: this.connectGoogle
          }
        },
        {
          label: "Add the Widget to your theme",
          description:
            "Add our widget to your theme to showcase your Google reviews and build trust with your customers",
          isOpen: false,

          flag: "isWidgetInstalled",
          button: {
            label: "Configure",
            onClick: this.gotoWidgetConfig
          }
        },
        {
          label: "Setup Review Request Email",
          isOpen: false,
          description:
            "Setup automated review request emails to collect more Google reviews from your customers after they make a purchase",

          flag: "isReviewRequestConfigured",
          button: {
            label: "Configure",
            onClick: this.gotoRequestConfigure
          }
        }
      ]
    };
  },
  computed: {
    onboardingFlags() {
      const config = this.$store.getters.getConfig;
      if (config) {
        return config.onboardingFlags;
      }
      return {};
    },
    isDismissed() {
      return this.onboardingFlags && this.onboardingFlags.isOnboardingDismissed;
    },
    completedValue() {
      const flags = this.onboardingFlags;
      const features = this.features;
      const totalFeatures = features.length;
      let completedFeatures = 0;
      this.features.forEach(feature => {
        if (flags && flags[feature.flag]) {
          completedFeatures += 1;
        }
      });
      const percentage = completedFeatures / totalFeatures;
      return { totalFeatures, completedFeatures, percentage };
    }
  },
  created() {
    window.addEventListener("click", this.handleOutsideClick);
  },
  beforeDestroy() {
    window.removeEventListener("click", this.handleOutsideClick);
  },
  methods: {
    handleOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.dropDropdown = false;
        this.isOpen = false;
      }
    },
    toggleFeature(index) {
      // Toggle the clicked feature
      this.features[index].isOpen = !this.features[index].isOpen;

      // Close all other features
      this.features.forEach((feature, i) => {
        if (i !== index) {
          feature.isOpen = false;
        }
      });
    },
    updateOpenFeatures() {
      // Find first incomplete feature
      const firstIncompleteIndex = this.features.findIndex(
        feature => !this.onboardingFlags[feature.flag]
      );

      // Close all features
      this.features.forEach(feature => {
        feature.isOpen = false;
      });

      // If found an incomplete feature, open it
      if (firstIncompleteIndex !== -1) {
        this.features[firstIncompleteIndex].isOpen = true;
      }
    },
    gotoRoute(route) {
      if (window.appBridgeApp) {
        history.pushState({}, null, route.path);
        this.$router.push({ path: route.path });
      } else {
        this.$router.push({ name: route.routeName });
      }

      this.dropDropdown = false;
      this.isOpen = false;
    },
    connectGoogle() {
      console.log("connect google");
      this.$store.dispatch("connectGoogle").then(response => {
        if (response.url) {
          console.log("connect to google", response.url);
          open(response.url, "_top");
        }
      });
    },
    gotoWidgetConfig() {
      this.$router.push({ name: "widget" });
    },
    gotoRequestConfigure() {
      this.$router.push({ name: "FullyAutomated" });
    },
    dismissOnboarding() {
      this.$store.dispatch("updateOnboardingFlag", {
        requestParams: {
          flag: "isOnboardingDismissed",
          value: true
        }
      });
    }
  },

  watch: {
    onboardingFlags: {
      handler() {
        this.updateOpenFeatures();
      },
      immediate: true,
      deep: true
    }
  }
};
</script>

<style lang="scss" scoped>
.progress-bar {
  width: 100%;
}
</style>
